/**
 * AI Assist Button Component
 * Reusable button for triggering AI-powered field suggestions
 */

import { Sparkles, Loader2 } from 'lucide-react';

export interface AiAssistButtonProps {
  /** Callback when button is clicked */
  onGenerate: () => void;
  /** Whether generation is in progress */
  loading?: boolean;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Tooltip text */
  tooltip?: string;
  /** Size variant */
  size?: 'sm' | 'md';
}

/**
 * AI Assist Button - displays a sparkle icon that triggers AI generation
 */
export function AiAssistButton({
  onGenerate,
  loading = false,
  disabled = false,
  tooltip = 'Generate with AI',
  size = 'sm',
}: AiAssistButtonProps): JSX.Element {
  const sizeClasses = {
    sm: 'p-1 rounded',
    md: 'p-1.5 rounded-md',
  };

  const iconSize = size === 'sm' ? 14 : 16;

  return (
    <button
      type="button"
      onClick={onGenerate}
      disabled={loading || disabled}
      title={tooltip}
      className={`
        ${sizeClasses[size]}
        inline-flex items-center justify-center
        text-purple-600 dark:text-purple-400
        hover:bg-purple-100 dark:hover:bg-purple-900/30
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-colors duration-150
        focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1
      `}
    >
      {loading ? (
        <Loader2 size={iconSize} className="animate-spin" />
      ) : (
        <Sparkles size={iconSize} />
      )}
    </button>
  );
}

/**
 * Form label with integrated AI assist button
 */
export interface AiLabelProps {
  /** Label text */
  label: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether to show the AI button */
  showAiButton?: boolean;
  /** AI button props */
  aiButtonProps?: Omit<AiAssistButtonProps, 'size'>;
  /** HTML for attribute */
  htmlFor?: string;
}

export function AiLabel({
  label,
  required = false,
  showAiButton = false,
  aiButtonProps,
  htmlFor,
}: AiLabelProps): JSX.Element {
  return (
    <div className="flex items-center gap-2 mb-2">
      <label htmlFor={htmlFor} className="block text-sm font-medium">
        {label}
        {required && ' *'}
      </label>
      {showAiButton && aiButtonProps && (
        <AiAssistButton {...aiButtonProps} size="sm" />
      )}
    </div>
  );
}

export default AiAssistButton;
